import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import api from "../../services/api";

const Settings = () => {
  const { storeId } = useParams();
  const [settings, setSettings] = useState({
    name: "",
    address: "",
    phoneNumber: "",
    isActive: true,
    openingTime: "",
    closingTime: "",
    taxRate: 10,
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        // 店舗基本情報と追加設定を両方取得
        const [storeRes, settingsRes] = await Promise.all([
          api.get(`/api/stores/${storeId}`),
          api.get(`/api/stores/${storeId}/store-settings`).catch(() => ({ data: {} })),
        ]);

        const storeData = storeRes.data || {};
        const settingsData = settingsRes.data || {};

        setSettings({
          name: storeData.name || "",
          address: storeData.address || "",
          phoneNumber: storeData.phone || storeData.phoneNumber || "",
          isActive: storeData.status === "active" || storeData.isActive !== false,
          openingTime: settingsData.openingTime || "",
          closingTime: settingsData.closingTime || "",
          taxRate: settingsData.standardTaxRate || 10,
        });
      } catch (error) {
        console.error("設定の取得に失敗しました:", error);
        setError("設定の取得に失敗しました");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitchChange = (e) => {
    setSettings((prev) => ({
      ...prev,
      isActive: e.target.checked,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.put(`/api/stores/${storeId}/store-settings`, settings);
      setSuccess(true);
      setError(null);
    } catch (error) {
      console.error("設定の更新に失敗しました:", error);
      setError("設定の更新に失敗しました");
      setSuccess(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box aria-busy={loading || saving}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Typography variant="h4" gutterBottom>
        店舗設定
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          設定を更新しました
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  基本設定
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="店舗名"
                      name="name"
                      value={settings.name}
                      onChange={handleChange}
                      required
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="住所"
                      name="address"
                      value={settings.address}
                      onChange={handleChange}
                      required
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="電話番号"
                      name="phoneNumber"
                      value={settings.phoneNumber}
                      onChange={handleChange}
                      required
                      disabled={saving}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  営業設定
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.isActive}
                          onChange={handleSwitchChange}
                          name="isActive"
                          disabled={saving}
                        />
                      }
                      label="営業中"
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="営業開始時間"
                      name="openingTime"
                      type="time"
                      value={settings.openingTime}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="営業終了時間"
                      name="closingTime"
                      type="time"
                      value={settings.closingTime}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="消費税率 (%)"
                      name="taxRate"
                      type="number"
                      value={settings.taxRate}
                      onChange={handleChange}
                      InputProps={{ inputProps: { min: 0, max: 100 } }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={saving}
            >
              {saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "設定を保存"
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default Settings;
